import { Maximize2, X } from "lucide-react";
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { CurvaDocument } from "@/document/model/document";
import { findSceneNode } from "@/document/model/document";
import type { ScriptPreviewInputBuffer } from "@/scripting/scriptPreviewInput";
import { Renderer } from "@/renderer/Renderer";
import { resizeObserverBorderBoxSize } from "./resizeObserverGeometry";
import { renderScriptPreviewToCanvas, scriptPreviewRenderScale } from "./scriptPreviewRender";

interface ScriptPreviewModalProps {
  frameId: string;
  frameName?: string;
  document: CurvaDocument;
  input: ScriptPreviewInputBuffer;
  onClose: () => void;
}

export function ScriptPreviewModal({
  frameId,
  frameName,
  document,
  input,
  onClose,
}: ScriptPreviewModalProps) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const letterboxRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const renderGenerationRef = useRef(0);
  const inputRef = useRef(input);
  inputRef.current = input;
  const [isFullscreen, setIsFullscreen] = useState(false);
  /** CSS pixel width of the letterbox — layout stays CSS-only; this only drives backing-store DPI. */
  const [displayWidthCss, setDisplayWidthCss] = useState(1);

  const frame = findSceneNode(document, frameId);
  const frameWidth = frame?.type === "frame" ? Math.max(1, Math.abs(frame.bounds.width)) : 1;
  const frameHeight = frame?.type === "frame" ? Math.max(1, Math.abs(frame.bounds.height)) : 1;
  const title = frameName || frame?.name || "Script preview";
  const renderScale = scriptPreviewRenderScale(frameWidth, displayWidthCss);

  useEffect(() => {
    input.setFrameSize(frameWidth, frameHeight);
  }, [frameHeight, frameWidth, input]);

  useEffect(() => {
    const syncFullscreen = () => {
      setIsFullscreen(globalThis.document.fullscreenElement === stageRef.current);
    };
    globalThis.document.addEventListener("fullscreenchange", syncFullscreen);
    return () => globalThis.document.removeEventListener("fullscreenchange", syncFullscreen);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      event.stopPropagation();
      // Let the browser leave fullscreen on Escape before the script sees it.
      if (event.key === "Escape" && globalThis.document.fullscreenElement) {
        return;
      }
      event.preventDefault();
      inputRef.current.noteKeyDown(event);
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      event.stopPropagation();
      if (event.key === "Escape" && globalThis.document.fullscreenElement) {
        return;
      }
      event.preventDefault();
      inputRef.current.noteKeyUp(event);
    };
    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("keyup", handleKeyUp, true);
    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("keyup", handleKeyUp, true);
    };
  }, []);

  useEffect(() => {
    const letterbox = letterboxRef.current;
    if (!letterbox) {
      return;
    }
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) {
        return;
      }
      const width = Math.max(1, Math.round(resizeObserverBorderBoxSize(entry).width));
      setDisplayWidthCss(prev => (prev === width ? prev : width));
    });
    observer.observe(letterbox);
    setDisplayWidthCss(Math.max(1, Math.round(letterbox.getBoundingClientRect().width)));
    return () => observer.disconnect();
  }, [frameHeight, frameWidth]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || displayWidthCss < 2) {
      return;
    }

    const generation = renderGenerationRef.current + 1;
    renderGenerationRef.current = generation;
    const renderer = rendererRef.current ?? new Renderer(canvas, { alpha: false, preserveDrawingBuffer: true });
    rendererRef.current = renderer;

    void renderScriptPreviewToCanvas(renderer, {
      document,
      frameId,
      scale: renderScale,
    }).then(() => {
      if (renderGenerationRef.current !== generation) {
        return;
      }
    }).catch(error => {
      if (renderGenerationRef.current === generation) {
        console.warn("Failed to render script preview", error);
      }
    });
  }, [document, displayWidthCss, frameId, renderScale]);

  useEffect(() => {
    return () => {
      if (globalThis.document.fullscreenElement) {
        void globalThis.document.exitFullscreen();
      }
      rendererRef.current?.dispose();
      rendererRef.current = null;
    };
  }, []);

  function toggleFullscreen(): void {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    if (globalThis.document.fullscreenElement) {
      void globalThis.document.exitFullscreen();
      return;
    }
    void stage.requestFullscreen();
  }

  function syncPointer(event: ReactPointerEvent<HTMLCanvasElement>): void {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    input.notePointerMove(event.clientX, event.clientY, canvas);
    input.notePointerButtons(event.buttons);
  }

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Script preview"
    >
      <div className="flex h-full max-h-full w-full max-w-5xl flex-col overflow-hidden rounded border border-white/10 bg-[#15191f] shadow-2xl">
        <div className="flex h-11 shrink-0 items-center gap-2 border-b border-white/10 px-3 text-slate-300">
          <div className="min-w-0 flex-1 truncate text-[12px] font-medium text-slate-100">
            {title}
            <span className="ml-2 font-normal text-slate-500">Running script · 30 fps</span>
          </div>
          <button
            type="button"
            aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            onClick={toggleFullscreen}
            className="grid size-8 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100"
          >
            <Maximize2 className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Stop script"
            title="Stop script"
            onClick={onClose}
            className="grid size-8 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden bg-[#0f1216] p-6">
          {/*
            Layout/letterbox = CSS only. ResizeObserver only updates the WebGL
            backing-store scale so the bitmap matches the displayed CSS size.
          */}
          <div
            ref={stageRef}
            className="grid h-full w-full place-items-center overflow-hidden bg-[#0a0c10] [:fullscreen]:h-screen [:fullscreen]:w-screen"
            style={{ containerType: "size" }}
          >
            <div
              ref={letterboxRef}
              className="relative overflow-hidden"
              style={{
                aspectRatio: `${frameWidth} / ${frameHeight}`,
                width: `min(100cqw, calc(100cqh * ${frameWidth} / ${frameHeight}))`,
                height: `min(100cqh, calc(100cqw * ${frameHeight} / ${frameWidth}))`,
              }}
            >
              <canvas
                className="block h-full w-full"
                ref={canvasRef}
                onPointerMove={syncPointer}
                onPointerDown={event => {
                  event.currentTarget.setPointerCapture(event.pointerId);
                  syncPointer(event);
                }}
                onPointerUp={syncPointer}
                onPointerCancel={() => {
                  input.notePointerLeave();
                }}
                onPointerLeave={() => {
                  input.notePointerLeave();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
